import { useState, type FormEvent, type MouseEvent } from "react";
import { ArrowRight, AtSign, Check, ChevronDown, Circle, Download, Facebook, FileText, Image as ImageIcon, Instagram, Link2, MessageSquareText, Music2, PlayCircle, Plus, Tv2, Youtube, type LucideIcon } from "lucide-react";
import { getHomeCopy } from "./homeI18n";
import type { Locale } from "./i18n";
import { beginPlatformHandoff, platformDownloaderPath } from "./platformHandoff";
import { detectPlatform, isSupportedPlatformURL, type PlatformID } from "./platforms";
import { localizedPath } from "./route";
import { getGrabSiloDesktopCopy, getHomeDesktopPromo, grabSiloDesktopLocale } from "./grabSiloDesktopI18n";

export interface HomePlatformFlags {
  tiktokEnabled: boolean;
  douyinEnabled: boolean;
  xEnabled: boolean;
  bilibiliEnabled: boolean;
}

interface PlatformItem {
  id: PlatformID;
  label: string;
  icon: LucideIcon;
  color: string;
}

export const homePlatformOrder: readonly PlatformItem[] = [
  { id: "youtube", label: "YouTube", icon: Youtube, color: "#ff0033" },
  { id: "instagram", label: "Instagram", icon: Instagram, color: "#d93b79" },
  { id: "tiktok", label: "TikTok", icon: Music2, color: "#111827" },
  { id: "facebook", label: "Facebook", icon: Facebook, color: "#0866ff" },
  { id: "threads", label: "Threads", icon: MessageSquareText, color: "#111827" },
  { id: "x", label: "X", icon: AtSign, color: "#111827" },
  { id: "douyin", label: "Douyin", icon: Music2, color: "#111827" },
  { id: "bilibili", label: "Bilibili", icon: Tv2, color: "#00aeec" }
] as const;

function platformAvailable(platform: PlatformID, flags: HomePlatformFlags): boolean {
  if (platform === "tiktok") return flags.tiktokEnabled;
  if (platform === "douyin") return flags.douyinEnabled;
  if (platform === "x") return flags.xEnabled;
  if (platform === "bilibili") return flags.bilibiliEnabled;
  return true;
}

function enabledPlatforms(flags: HomePlatformFlags): readonly PlatformItem[] {
  return homePlatformOrder.filter((platform) => platformAvailable(platform.id, flags));
}

export function HomePlatformNav({ locale, flags, active, onNavigate }: { locale: Locale; flags: HomePlatformFlags; active?: PlatformID; onNavigate?: (destination: string) => void }) {
  const available = enabledPlatforms(flags);
  const primary = available.slice(0, 5);
  const more = available.slice(5);
  const navigate = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!onNavigate || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    onNavigate(event.currentTarget.href);
  };
  return (
    <nav className="home-platform-nav" aria-label="Downloaders">
      {primary.map((platform) => <a key={platform.id} className={active === platform.id ? "active" : undefined} aria-current={active === platform.id ? "page" : undefined} href={platformDownloaderPath(locale, platform.id)} onClick={navigate}>{platform.label}</a>)}
      {more.length > 0 && (
        <details className={`home-platform-more${more.some((platform) => platform.id === active) ? " active" : ""}`}>
          <summary><span>More</span><ChevronDown size={14} aria-hidden="true" /></summary>
          <div>
            {more.map((platform) => <a key={platform.id} className={active === platform.id ? "active" : undefined} aria-current={active === platform.id ? "page" : undefined} href={platformDownloaderPath(locale, platform.id)} onClick={navigate}>{platform.label}</a>)}
          </div>
        </details>
      )}
    </nav>
  );
}

export function HomePage({ locale, flags }: { locale: Locale; flags: HomePlatformFlags }) {
  const copy = getHomeCopy(locale);
  const desktopCopy = getGrabSiloDesktopCopy(locale);
  const desktopPromo = getHomeDesktopPromo(locale);
  const [input, setInput] = useState("");
  const [error, setError] = useState("");
  const platforms = enabledPlatforms(flags);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError("");
    const candidate = input.trim();
    const platform = detectPlatform(candidate);
    if (!platform || !isSupportedPlatformURL(platform, candidate)) {
      setError(copy.invalidLink);
      return;
    }
    if (!platformAvailable(platform, flags)) {
      setError(copy.unavailablePlatform);
      return;
    }
    if (!beginPlatformHandoff(locale, platform, candidate)) setError(copy.invalidLink);
  };

  return (
    <main className="home-main">
      <section className="home-hero" aria-labelledby="home-title">
        <Circle className="home-orbit-ring" strokeWidth={0.8} aria-hidden="true" />
        <Circle className="home-orbit home-orbit-coral" fill="currentColor" strokeWidth={0} aria-hidden="true" />
        <Circle className="home-orbit home-orbit-blue" fill="currentColor" strokeWidth={0} aria-hidden="true" />
        <Circle className="home-orbit home-orbit-coral-lower" fill="currentColor" strokeWidth={0} aria-hidden="true" />
        <div className="home-hero-copy">
          <p>{copy.eyebrow}</p>
          <h1 id="home-title">{copy.heroTitle}</h1>
          <div>{copy.heroLead}</div>
        </div>

        <form className="home-command" onSubmit={submit} noValidate>
          <label className="sr-only" htmlFor="home-url">{copy.inputLabel}</label>
          <div className="home-command-row">
            <div className="home-command-field">
              <Link2 size={22} aria-hidden="true" />
              <input id="home-url" type="url" inputMode="url" autoComplete="url" value={input} onChange={(event) => setInput(event.target.value)} placeholder={copy.placeholder} aria-describedby={error ? "home-url-error" : "home-url-note"} />
            </div>
            <button type="submit"><Download size={21} aria-hidden="true" /><span>{copy.submit}</span></button>
          </div>
          <p id="home-url-note"><span>{copy.publicOnly}</span><span aria-hidden="true">·</span><span>{copy.noSignup}</span></p>
          {error && <p className="home-command-error" id="home-url-error" role="alert">{error}</p>}
        </form>

        <nav className="home-platform-ribbon" aria-label={copy.worksWith}>
          <strong>{copy.worksWith}</strong>
          {platforms.map(({ id, label, icon: PlatformIcon, color }) => (
            <a key={id} href={platformDownloaderPath(locale, id)}>
              <PlatformIcon aria-hidden="true" style={{ color }} />
              <span>{label}</span>
            </a>
          ))}
        </nav>
      </section>

      <section className="home-desktop-promo" aria-labelledby="home-desktop-title">
        <header className="home-desktop-head">
          <p>{desktopCopy.promoKicker}</p>
          <h2 id="home-desktop-title">{desktopPromo.title}</h2>
          <span>{desktopPromo.subtitle}</span>
        </header>
        <div className="home-desktop-compare">
          <div className="home-desktop-col is-web">
            <h3>{desktopPromo.webLabel}</h3>
            <ul>
              {desktopPromo.webPoints.map((point) => <li key={point}><Circle aria-hidden="true" size={8} />{point}</li>)}
            </ul>
          </div>
          <div className="home-desktop-col is-desktop">
            <h3>{desktopPromo.desktopLabel}</h3>
            <ul>
              {desktopPromo.desktopPoints.map((point) => <li key={point}><Check aria-hidden="true" size={15} />{point}</li>)}
            </ul>
          </div>
        </div>
        <div className="home-desktop-action">
          <a href={localizedPath(grabSiloDesktopLocale(locale), "desktop")}>{desktopPromo.cta}<ArrowRight size={16} /></a>
          <small>{desktopPromo.small}</small>
        </div>
      </section>

      <section className="home-proof" id="how" aria-labelledby="home-how-title">
        <div className="home-steps">
          <h2 id="home-how-title">{copy.howTitle}</h2>
          <ol>
            {copy.steps.map(([title, body], index) => (
              <li key={title}>
                <span>{index + 1}</span>
                <div><strong>{title}</strong><p>{body}</p></div>
              </li>
            ))}
          </ol>
        </div>

        <div className="home-result-preview" aria-label="Download result preview">
          <div className="home-preview-row home-video-preview-row">
            <div className="home-preview-label"><PlayCircle size={16} aria-hidden="true" /><strong>{copy.previewVideo}</strong><span>MP4</span></div>
            <div className="home-preview-content">
              <div className="home-video-thumb"><img src="/home/coast-video.webp" alt="" width="640" height="360" /><span>00:32</span></div>
              <div><strong>coastal-views.mp4</strong><span>1080p · 32.4 MB</span></div>
              <a href="#home-url" aria-label={copy.submit}><Download size={17} aria-hidden="true" /></a>
            </div>
          </div>
          <div className="home-preview-row home-photo-preview-row">
            <div className="home-preview-label"><ImageIcon size={16} aria-hidden="true" /><strong>{copy.previewPhotos}</strong><span>JPG</span></div>
            <div className="home-photo-strip">
              <img className="home-photo-coast-left" src="/home/coast-video.webp" alt="" width="640" height="360" />
              <img src="/home/sunset-photo.webp" alt="" width="400" height="300" />
              <img src="/home/lake-photo.webp" alt="" width="400" height="300" />
              <img className="home-photo-coast-right" src="/home/coast-video.webp" alt="" width="640" height="360" />
              <img className="home-photo-sunset-right" src="/home/sunset-photo.webp" alt="" width="400" height="300" />
              <span>+7</span>
              <a href="#home-url" aria-label={copy.submit}><Download size={17} aria-hidden="true" /></a>
            </div>
          </div>
          <div className="home-preview-row home-text-preview-row">
            <div className="home-preview-label"><FileText size={16} aria-hidden="true" /><strong>{copy.previewText}</strong><span>TXT</span></div>
            <div className="home-preview-content"><p>{copy.previewTextValue}</p><a href="#home-url" aria-label={copy.submit}><Download size={17} aria-hidden="true" /></a></div>
          </div>
        </div>
      </section>

      <section className="home-faq" id="faq" aria-labelledby="home-faq-title">
        <div><p>{copy.eyebrow}</p><h2 id="home-faq-title">{copy.faqTitle}</h2></div>
        <div>
          {copy.faqs.map(([question, answer]) => <details key={question}><summary><span>{question}</span><Plus size={20} aria-hidden="true" /></summary><p>{answer}</p></details>)}
        </div>
      </section>
    </main>
  );
}
